/**
 * Shared presence utilities used by both the banner facepile and the menu presence indicators.
 */
import {
	type Character,
	GameMaster,
	type PresenceUser,
	type User,
	useAppContext,
} from '@datapad/data-model';
import { Stack, Typography } from '@mui/material';
import { Tree } from 'fluid-framework';
import type React from 'react';

/**
 * Tooltip content showing an avatar/icon, a label, and an optional subtitle.
 *
 * @remarks
 * When an `onClick` handler is provided the row becomes clickable with a pointer
 * cursor and a subtle hover effect, enabling navigation (e.g. to a character page).
 */
export function PresenceTooltip(props: {
	icon: React.ReactNode;
	label: string;
	subtitle?: string;
	onClick?: () => void;
}): React.ReactElement {
	const { icon, label, subtitle, onClick } = props;
	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={1}
			onClick={onClick}
			sx={{
				p: 0.5,
				...(onClick && { cursor: 'pointer', '&:hover': { opacity: 0.8 } }),
			}}
		>
			{icon}
			<Stack>
				<Typography variant="body2">{label}</Typography>
				{subtitle && (
					<Typography variant="caption" color="text.secondary">
						{subtitle}
					</Typography>
				)}
			</Stack>
		</Stack>
	);
}

/**
 * Resolved presence user data returned by {@link useResolvedPresenceUser}.
 */
export interface ResolvedPresenceUser {
	/**
	 * The matched app user, if found.
	 */
	matchedUser: User | undefined;

	/**
	 * The session character, if the user has one selected.
	 */
	character: Character | undefined;

	/**
	 * Whether the matched user is a Game Master.
	 */
	isGameMaster: boolean;
}

/**
 * Resolves a {@link PresenceUser} to app data by matching against the user list
 * and optionally looking up the session character.
 */
export function useResolvedPresenceUser(user: PresenceUser): ResolvedPresenceUser {
	const appContext = useAppContext();

	const allUsers = appContext.getAllUsers();
	const matchedUser = user.value.userId
		? allUsers.find((u) => u.id === user.value.userId)
		: undefined;

	// Resolve session character if the user has one
	let character: Character | undefined;
	if (user.value.sessionCharacterId) {
		try {
			const allCharacters = appContext.getAllCharacters();
			character = allCharacters.find((c) => c.id === user.value.sessionCharacterId);
		} catch {
			// Character not found - fall through to undefined
		}
	}

	const isGameMaster = matchedUser !== undefined && Tree.is(matchedUser, GameMaster);

	return { matchedUser, character, isGameMaster };
}
